/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { MouseEvent, useMemo, useState } from "react";

import { BEM, RENDER_MODE, TRootComponentListSchema } from "@adapptio/lib-components";
import { IRenderContext, IViewportConfig, Main } from "@adapptio/lib-core";
import { TGetBlueprintSchemaModel } from "@adapptio/lib-blueprint";
import { IResizeObserverProps, ResizeObserver } from "./ResizeObserver";
import { v4 as uuid_v4 } from "uuid";

/**
 * Component props
 */
export interface IViewPreviewProps {
	renderContext: IRenderContext;
	viewportConfig?: IViewportConfig;
	mainComponentListModel?: TGetBlueprintSchemaModel<TRootComponentListSchema>;
	onRootClick?: (ev: MouseEvent) => void;
	className?: string;
	onResize?: IResizeObserverProps["onResize"];
}

/**
 * Displays a preview of app's UI view
 *
 * @param props Component props
 */
export const ViewPreview = React.forwardRef<HTMLDivElement, IViewPreviewProps>(
	({ renderContext, viewportConfig, mainComponentListModel, onRootClick, className, onResize }, ref) => {
		const [ appRootId ] = useState(() => uuid_v4());

		const baseClass = BEM("edt-view-preview");

		if (className) {
			baseClass.add(className);
		}

		const sizeStyle = useMemo<React.CSSProperties>(() => {
			return {
				minWidth: viewportConfig?.minWidth,
				maxWidth: viewportConfig?.maxWidth,
				width: viewportConfig?.width || "100%",
				height: viewportConfig?.height || "100%"
			};
		}, [
			viewportConfig?.width,
			viewportConfig?.height,
			viewportConfig?.minWidth,
			viewportConfig?.maxWidth
		]);

		return (
			<div className={baseClass.toClassName()}>
				<div ref={ref} className={baseClass.element("wrapper").toClassName()}>
					<ResizeObserver onResize={onResize}>
						<div className={baseClass.element("container").toClassName()} style={sizeStyle}>
							<div className={baseClass.element("cell").toClassName()}>
								<Main
									renderContext={renderContext}
									renderMode={RENDER_MODE.BROWSER}
									wasRenderedWithSsr={false}
									appRootId={appRootId}
									viewportConfig={viewportConfig}
									mainComponentListModel={mainComponentListModel}
									onRootClick={onRootClick}
								/>
							</div>
						</div>
					</ResizeObserver>
				</div>
			</div>
		);
	}
);
